/**
 * screens/SettingsScreen.tsx
 * Controls · Agent Settings (operator) — persona, hours, escalation, CSAT,
 * notifications, plan & billing, compliance. Verbatim from HTML (~558-692),
 * max-width 720.
 *
 * Wired to sk-orchestrator: getSettings()/updateSettings() own the editable
 * tabs; Plan & Billing is Platform's plan card; compliance is static.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, TextInput, ScrollView } from 'react-native';
import { useTheme } from '../context/themeContext';
import Icon from '../components/icon';
import { Chip, Switch, ScreenBody } from '../components/voiceUi';
import PlanCardPanel from '../components/PlanCardPanel';
import { getSettings, updateSettings, AgentSettings } from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

type Tab = 'persona' | 'hours' | 'escalation' | 'csat' | 'notif' | 'billing' | 'compliance';
const TABS: [Tab, string][] = [
  ['persona', 'Persona'], ['hours', 'Hours'], ['escalation', 'Escalation'],
  ['csat', 'CSAT'], ['notif', 'Notifications'], ['billing', 'Plan & Billing'], ['compliance', 'Compliance'],
];

const DAYS: [string, string][] = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
// Real backend VoiceId enum values (persona.voiceId) with friendly labels. The
// unified/Gemini pipeline is primary, so Gemini voices lead; Sarvam/ElevenLabs
// voices are for the composable pipeline. The SAVED value is the enum, not the label.
const VOICE_OPTS: { value: string; label: string }[] = [
  { value: 'AOEDE_GEMINI', label: 'Aoede · warm (Gemini)' },
  { value: 'KORE_GEMINI', label: 'Kore · neutral (Gemini)' },
  { value: 'PUCK_GEMINI', label: 'Puck · bright (Gemini)' },
  { value: 'CHARON_GEMINI', label: 'Charon · deep (Gemini)' },
  { value: 'LEDA_GEMINI', label: 'Leda · soft (Gemini)' },
  { value: 'ZEPHYR_GEMINI', label: 'Zephyr · light (Gemini)' },
  { value: 'KAVYA_SARVAM', label: 'Kavya · Indic (Sarvam)' },
  { value: 'RAJAN_SARVAM', label: 'Rajan · Indic (Sarvam)' },
  { value: 'PRIYA_ELEVENLABS', label: 'Priya · premium (ElevenLabs)' },
];
const voiceLabel = (value: string) => VOICE_OPTS.find(o => o.value === value)?.label ?? value;

// Field is defined at MODULE scope (not inside SettingsScreen) so its component
// identity is stable across renders. When it was defined inline, every keystroke
// re-created the component type → React remounted the TextInput → focus was lost
// after each character, making inputs feel uneditable. It reads its own styles
// from the theme so no per-render style props are threaded through.
function Field({ label, children, help, style }: { label: string; children: React.ReactNode; help?: string; style?: object }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const labelStyle = { fontSize: 12, fontWeight: '500' as const, color: c.ink2, fontFamily: theme.font.sans };
  const helpStyle = { fontSize: 11, color: c.ink4, fontFamily: theme.font.sans };
  return (
    <View style={[{ gap: 6 }, style]}>
      <Text style={labelStyle}>{label}</Text>
      {children}
      {help && <Text style={helpStyle}>{help}</Text>}
    </View>
  );
}
const AFTER_HOURS = ['Take a voicemail follow-up', 'Transfer to on-call number', 'Play closed message and end'];
const NOTIF_ROWS: [string, string, string][] = [
  ['dailySummary', 'Daily summary email', 'A digest of yesterday’s calls and outcomes'],
  ['lowCsat', 'Low-CSAT alert', 'Ping when a resolved call scores below threshold'],
  ['escalations', 'Escalation alert', 'Notify the moment a call transfers to a human'],
  ['missed', 'Missed / abandoned alert', 'Flag calls that dropped before resolution'],
];
const COMPLIANCE = [
  { title: 'PII masking', desc: 'Caller numbers and identifiers are masked in every log and transcript.' },
  { title: 'Retention floor', desc: 'Call records retained per contractual floor; deletion is append-only tombstoned.' },
  { title: 'DPDP consent', desc: 'Consent is captured on-call before any personal data is stored.' },
  { title: 'Cross-org isolation', desc: 'Row-level security guarantees one tenant can never read another’s data.' },
];

// Seed defaults — the initial render + fallback when the backend is silent.
// getSettings() replaces the editable-tab slices.
const SEED_PERSONA = { name: '', voice: VOICE_OPTS[0].value, hold: '', lane: 'unified' as 'unified' | 'composable' };
const SEED_HOURS: Record<string, any> = { mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false, open: '09:00', close: '18:00', after: AFTER_HOURS[0] };
const SEED_ESCALATION = { turnLimit: '6', tier1: 'Front desk', transfer: '+91 44 4012 8890' };
const SEED_CSAT = { enabled: true, resolvedOnly: true, alertBelow: '3' };
const SEED_NOTIF: Record<string, boolean> = { dailySummary: true, lowCsat: true, escalations: true, missed: true };

export default function SettingsScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const [tab, setTab] = useState<Tab>('persona');
  const [voiceOpen, setVoiceOpen] = useState(false); // Voice dropdown open state

  const [persona, setPersona] = useState(SEED_PERSONA);
  const [hours, setHours] = useState<Record<string, any>>(SEED_HOURS);
  const [escalation, setEscalation] = useState(SEED_ESCALATION);
  const [csat, setCsat] = useState(SEED_CSAT);
  const [notif, setNotif] = useState<Record<string, boolean>>(SEED_NOTIF);

  // Hydrate editable tabs from getSettings(); it falls back to seed values, so
  // partial payloads are safe.
  const load = useCallback(async () => {
    try {
      const { data: s } = await getSettings();
      if (s.persona) setPersona(p => ({ ...p, ...s.persona }));
      if (s.hours) setHours(h => ({ ...h, ...s.hours }));
      if (s.escalation) setEscalation(e => ({ ...e, ...s.escalation }));
      if (s.csat) setCsat(cs => ({ ...cs, ...s.csat }));
      if (s.notif) setNotif(n => ({ ...n, ...s.notif }));
    } catch (err) {
      handleApiError(err, 'Could not load settings');
    }
  }, []);

  // Console shell has no React Navigation focus hooks — plain mount effect.
  useEffect(() => { void load(); }, [load]);

  // Persist one tab's slice, then toast. Optimistic: state already holds the edit.
  const saveTab = async (tabKey: keyof AgentSettings, patch: any) => {
    try {
      const resp = await updateSettings({ [tabKey]: patch } as any);
      showSuccess(resp, 'Saved');
    } catch (err) {
      handleApiError(err, 'Could not save settings');
    }
  };

  // Shared field styles from HTML inputStyle / textareaStyle.
  const inputStyle = { height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, backgroundColor: c.card, color: c.ink, fontSize: 13, fontFamily: theme.font.sans } as any;
  const textareaStyle = { paddingHorizontal: 12, paddingVertical: 9, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, backgroundColor: c.card, color: c.ink, fontSize: 13, minHeight: 70, lineHeight: 13 * 1.5, fontFamily: theme.font.sans, textAlignVertical: 'top' } as any;
  const labelStyle = { fontSize: 12, fontWeight: '500' as const, color: c.ink2, fontFamily: theme.font.sans };
  const helpStyle = { fontSize: 11, color: c.ink4, fontFamily: theme.font.sans };

  // Cycling select-box styled like inputStyle.
  const SelectBox = ({ value, options, onChange }: { value: string; options: string[]; onChange: (v: string) => void }) => (
    <Pressable onPress={() => { const i = options.indexOf(value); onChange(options[(i + 1) % options.length]); }}
      style={[inputStyle, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
      <Text style={{ fontSize: 13, color: c.ink, fontFamily: theme.font.sans }}>{value}</Text>
      <Icon name="chev-d" size={14} color={c.ink3} />
    </Pressable>
  );

  const SwitchRow = ({ title, desc, on, onPress }: { title: string; desc: string; on: boolean; onPress: () => void }) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingHorizontal: 16, paddingVertical: 14, borderWidth: 1, borderColor: c.line, borderRadius: 10 }}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontWeight: '600', fontSize: 13, color: c.ink, fontFamily: theme.font.sans }}>{title}</Text>
        <Text style={{ fontSize: 11.5, color: c.ink3, fontFamily: theme.font.sans }}>{desc}</Text>
      </View>
      <Switch on={on} onPress={onPress} />
    </View>
  );

  return (
    <ScreenBody maxWidth={720}>
      {/* Tab bar */}
      <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: c.line, marginBottom: 22 }}>
        {TABS.map(([k, l]) => {
          const active = tab === k;
          return (
            <Pressable key={k} onPress={() => setTab(k)}
              style={{ paddingHorizontal: 13, paddingVertical: 9, borderBottomWidth: 2, borderBottomColor: active ? c.ink : 'transparent', marginBottom: -1 }}>
              <Text style={{ fontSize: 12.5, fontWeight: active ? '600' : '500', color: active ? c.ink : c.ink3, fontFamily: theme.font.sans }}>{l}</Text>
            </Pressable>
          );
        })}
      </View>

      {tab === 'persona' && (
        <View style={{ gap: 16 }}>
          <Field label="Agent name"><TextInput value={persona.name} onChangeText={v => setPersona(p => ({ ...p, name: v }))} onBlur={() => saveTab('persona', persona)} style={inputStyle} /></Field>
          {/* Voice is a real dropdown: click the trigger to open the full list,
              click a row to pick. Saved value is the VoiceId enum; the runtime
              maps it to a Gemini voice. */}
          {/* Elevated zIndex on the whole Voice row so the open panel overlays the
              Hold phrase field below it (sibling stacking — the inner zIndex alone
              isn't enough on RN-Web). */}
          <Field label="Voice" style={{ zIndex: voiceOpen ? 50 : 1 }}>
            <View style={{ position: 'relative' }}>
              <Pressable
                onPress={() => setVoiceOpen(o => !o)}
                style={[inputStyle, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
                <Text style={{ fontSize: 13, color: c.ink, fontFamily: theme.font.sans }}>{voiceLabel(persona.voice)}</Text>
                <Icon name={voiceOpen ? 'chev-u' : 'chev-d'} size={14} color={c.ink3} />
              </Pressable>
              {voiceOpen && (
                <View style={{ position: 'absolute', top: 42, left: 0, right: 0, maxHeight: 240, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, backgroundColor: c.card, overflow: 'hidden', zIndex: 50, elevation: 8, ...(theme.shadows.sm as object) }}>
                  <ScrollView>
                    {VOICE_OPTS.map(o => {
                      const on = o.value === persona.voice;
                      return (
                        <Pressable key={o.value}
                          onPress={() => { const next = { ...persona, voice: o.value }; setPersona(next); saveTab('persona', next); setVoiceOpen(false); }}
                          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, height: 38, backgroundColor: on ? c.paper2 : 'transparent' }}>
                          <Text style={{ fontSize: 13, color: c.ink, fontWeight: on ? '600' : '400', fontFamily: theme.font.sans }}>{o.label}</Text>
                          {on && <Icon name="check" size={14} color={c.ink} />}
                        </Pressable>
                      );
                    })}
                  </ScrollView>
                </View>
              )}
            </View>
          </Field>
          <Field label="Hold phrase"><TextInput value={persona.hold} onChangeText={v => setPersona(p => ({ ...p, hold: v }))} onBlur={() => saveTab('persona', persona)} multiline style={textareaStyle} /></Field>
          {/* Two-option pick, so it reads as a choice rather than an on/off
              switch — neither lane is "the default one turned off". */}
          <Field label="Voice pipeline">
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {([
                { v: 'unified' as const, label: 'Gemini Live', sub: 'One model. Fastest.' },
                { v: 'composable' as const, label: 'Composable', sub: 'Sarvam STT. Better Tamil.' },
              ]).map(o => {
                const on = persona.lane === o.v;
                return (
                  <Pressable
                    key={o.v}
                    onPress={() => { const next = { ...persona, lane: o.v }; setPersona(next); saveTab('persona', next); }}
                    style={{
                      flex: 1, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 7, borderWidth: 1,
                      borderColor: on ? c.accent : c.lineStrong,
                      backgroundColor: on ? c.accentSoft : c.card,
                    }}
                  >
                    <Text style={{ fontSize: 13, color: c.ink, fontFamily: theme.font.sans }}>{o.label}</Text>
                    <Text style={{ fontSize: 11, color: c.ink4, marginTop: 2, fontFamily: theme.font.sans }}>{o.sub}</Text>
                  </Pressable>
                );
              })}
            </View>
          </Field>
        </View>
      )}

      {tab === 'hours' && (
        <View style={{ gap: 18 }}>
          {/* Hours are owned by the product console (Hospitality/Salon settings), not voice —
              shown read-only here so the two can't drift. Only after-hours behaviour is voice-owned. */}
          <View>
            <Text style={{ ...labelStyle, marginBottom: 9 }}>Operating days</Text>
            <View style={{ flexDirection: 'row', gap: 7 }}>
              {DAYS.map(([k, l]) => {
                const on = hours[k];
                return (
                  <View key={k}
                    style={{ width: 44, height: 40, borderRadius: 8, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: on ? c.ink : c.line, backgroundColor: on ? c.ink : c.card, opacity: 0.7 }}>
                    <Text style={{ fontSize: 12, fontWeight: '600', color: on ? c.paper : c.ink3, fontFamily: theme.font.sans }}>{l}</Text>
                  </View>
                );
              })}
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 16 }}>
            <View style={{ flex: 1 }}><Field label="Opens" help="Managed in your business settings."><TextInput value={hours.open} editable={false} style={{ ...inputStyle, color: c.ink4 }} /></Field></View>
            <View style={{ flex: 1 }}><Field label="Closes" help="Managed in your business settings."><TextInput value={hours.close} editable={false} style={{ ...inputStyle, color: c.ink4 }} /></Field></View>
          </View>
          <Field label="After-hours behaviour"><SelectBox value={hours.after} options={AFTER_HOURS} onChange={v => { const next = { ...hours, after: v }; setHours(next); saveTab('hours', next); }} /></Field>
        </View>
      )}

      {tab === 'escalation' && (
        <View style={{ gap: 16 }}>
          <Field label="Turn limit before escalation" help="If the agent can’t resolve within this many turns, it escalates."><TextInput value={escalation.turnLimit} onChangeText={v => setEscalation(e => ({ ...e, turnLimit: v }))} onBlur={() => saveTab('escalation', escalation)} style={inputStyle} /></Field>
          <Field label="Tier 1 target"><TextInput value={escalation.tier1} onChangeText={v => setEscalation(e => ({ ...e, tier1: v }))} onBlur={() => saveTab('escalation', escalation)} style={inputStyle} /></Field>
          <Field label="Live-transfer number"><TextInput value={escalation.transfer} onChangeText={v => setEscalation(e => ({ ...e, transfer: v }))} onBlur={() => saveTab('escalation', escalation)} style={inputStyle} /></Field>
        </View>
      )}

      {tab === 'csat' && (
        <View style={{ gap: 14 }}>
          <SwitchRow title="Collect CSAT" desc="Ask for a 1–5 rating at the end of a call." on={csat.enabled} onPress={() => { const next = { ...csat, enabled: !csat.enabled }; setCsat(next); saveTab('csat', next); }} />
          <SwitchRow title="Resolved calls only" desc="Skip the survey on escalated or abandoned calls." on={csat.resolvedOnly} onPress={() => { const next = { ...csat, resolvedOnly: !csat.resolvedOnly }; setCsat(next); saveTab('csat', next); }} />
          <Field label="Low-CSAT alert threshold" help="Alert operators when a resolved call scores below this."><TextInput value={csat.alertBelow} onChangeText={v => setCsat(s => ({ ...s, alertBelow: v }))} onBlur={() => saveTab('csat', csat)} style={inputStyle} /></Field>
        </View>
      )}

      {tab === 'notif' && (
        <View style={{ gap: 12 }}>
          {NOTIF_ROWS.map(([k, l, d]) => (
            <SwitchRow key={k} title={l} desc={d} on={notif[k]} onPress={() => { const next = { ...notif, [k]: !notif[k] }; setNotif(next); saveTab('notif', next); }} />
          ))}
        </View>
      )}

      {tab === 'billing' && <PlanCardPanel />}

      {tab === 'compliance' && (
        <View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 11, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 9, marginBottom: 14, backgroundColor: c.paper2 }}>
            <Icon name="lock" size={15} color={c.ink} />
            <Text style={{ fontSize: 12.5, fontWeight: '500', color: c.ink, fontFamily: theme.font.sans }}>Enforced by Softknack — visible for trust, not toggleable.</Text>
          </View>
          <View style={{ gap: 10 }}>
            {COMPLIANCE.map(item => (
              <View key={item.title} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 11, paddingHorizontal: 16, paddingVertical: 14, borderWidth: 1, borderColor: c.line, borderRadius: 10, backgroundColor: c.card }}>
                <View style={{ marginTop: 1 }}><Icon name="check" size={16} color={c.ink} /></View>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontWeight: '600', fontSize: 13, marginBottom: 2, color: c.ink, fontFamily: theme.font.sans }}>{item.title}</Text>
                  <Text style={{ fontSize: 12, color: c.ink3, fontFamily: theme.font.sans }}>{item.desc}</Text>
                </View>
                <Chip kind="mono">enforced</Chip>
              </View>
            ))}
          </View>
        </View>
      )}
    </ScreenBody>
  );
}
